import React, { useState, useEffect } from 'react';
import { Button, Col, Container, Row, ToggleButton } from 'react-bootstrap';
import { FileUpload, FileInboxSelector } from '../../components/FileUpload';
import { InboxFolderList } from '../../helpers/InboxFolder';
import Overlay from '../../components/Overlay/Overlay';
import MainLayout from '../../layouts/MainLayout';
import languageManager from '../../helpers/Language';
import { useNavigate } from 'react-router';
import { UploadManager } from '../../helpers/FileUploadManager';
import CheckedIcon from 'react-ionicons/lib/CheckmarkCircleOutline';
import UncheckedIcon from 'react-ionicons/lib/EllipseOutline';
import UserAnalytic from '../../helpers/UserAnalytic';
import { getBucketList } from '../../api/actions/Drive';
import PermissionManager from '../../helpers/PermissionManager';
import { connect } from 'react-redux';
import { Cloud } from 'react-ionicons';
import './UploadCenter.scss';
import { DOMAIN_NAME } from '../../config/default';
import InMemoryCache, { EXPIRES_IN } from '../../helpers/InMemoryCache';
import FileBucketSelector from '../../components/FileUpload/FileBucketSelector';
import { getOctetsValueFromString } from '../../helpers/Drive';

const ShouldUseBucketList = () => {
    // TODO: Use a configuration file instead?
    return DOMAIN_NAME === 'FIDUCIAL_JADE';
};

export const DEFAULT_INBOX = 0;
export const DEFAULT_UPLOAD_LIMIT_OBJ = {
    strValue: '2M',
    numValue: 2000000,
};
export const MainUploadManager = new UploadManager(
    DEFAULT_UPLOAD_LIMIT_OBJ.numValue
);

export const DFLT_ICO_PROPS = {
    height: '28px',
    width: '28px',
    color: 'inherit',
    className: 'icon-cls',
};

const DSToggleButton = ({ checked, onChange, children }) => {
    return (
        <ToggleButton
            variant={checked ? 'success' : 'outline-secondary'}
            checked={checked}
            value="1"
            type="checkbox"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.target.blur();
                onChange(!checked);
            }}
        >
            {checked ? (
                <CheckedIcon {...DFLT_ICO_PROPS} />
            ) : (
                <UncheckedIcon {...DFLT_ICO_PROPS} />
            )}{' '}
            {children}
        </ToggleButton>
    );
};

const UploadCenterLoadingState = ({ children, isLoading }) => {
    if (isLoading) {
        return (
            <div className="d-flex justify-content-center">
                <div
                    className="loader"
                    style={{ width: 128, height: 128, borderWidth: 4 }}
                />
            </div>
        );
    }
    return children;
};

const UploadCenter = ({ profile, session }) => {
    const navigateManager = useNavigate();
    const [uploadManagerState, setUploadManagerRef] = useState({
        uManager: MainUploadManager,
    });
    const [inboxFolder, setInboxFolder] = useState(
        InboxFolderList[DEFAULT_INBOX]
    );
    const [overlayStatus, setOverlayStatus] = useState(false);
    const [isAutoUpload, setAutoUpload] = useState(true);
    const [uploadSizeMax, setUploadSizeMax] = useState(
        DEFAULT_UPLOAD_LIMIT_OBJ
    );
    const [initialized, setInitialized] = useState(false);
    const [useBucketListSystem, setBucketListUsage] = useState(null);
    const [driveBucketList, setDriveBucketList] = useState(null);
    const [isLoadingPageConfig, setLoadingPageConfig] = useState(true);

    const handleAddFileToUploadList = async (files) => {
        const { uManager } = uploadManagerState;
        UserAnalytic.event({
            category: 'drive_upload',
            action: 'Add file to upload list',
            label: `Add files`,
        });
        if (inboxFolder.isBucket) {
            uManager.addBucketFiles(files, isAutoUpload, inboxFolder.id);
        } else {
            uManager.addFiles(files, isAutoUpload, inboxFolder.name);
        }
        setUploadManagerRef({ uManager });
    };

    const handleInboxSelected = (inboxElm) => {
        setInboxFolder(inboxElm);
    };

    const handleRemoveFile = (fileId) => {
        const { uManager } = uploadManagerState;
        UserAnalytic.event({
            category: 'drive_upload',
            action: 'Remove file from list',
            label: `File Id ${fileId}`,
        });
        uManager.removeFile(fileId);
        setUploadManagerRef({ uManager });
    };

    const handleAutoUpdate = (value) => {
        const { uManager } = uploadManagerState;
        UserAnalytic.event({
            category: 'drive_upload',
            action: 'Change auto upload status',
            label: `Set Update ${value}`,
        });
        if (value) {
            uManager.startUpload();
        }
        setAutoUpload(value);
    };

    const getUploadSizeMax = () => {
        let size = uploadSizeMax.strValue;
        const uploadSizeLimit = session.modules?.drive?.uploadSizeLimit;

        if (uploadSizeLimit) {
            size = uploadSizeLimit;
        }

        return {
            strValue: size,
            numValue: getOctetsValueFromString(size),
        };
    };

    const loadBucketList = async () => {
        const key = 'bucket_list';
        let bucketList = InMemoryCache.get(key);
        if (!bucketList) {
            bucketList = await getBucketList();
            InMemoryCache.set(key, bucketList, EXPIRES_IN.MEDIUM);
        }
        return bucketList;
    };

    useEffect(() => {
        const init = async () => {
            const { uManager } = uploadManagerState;

            let uMaxSize = uploadSizeMax;
            if (!uMaxSize || !initialized) {
                uMaxSize = getUploadSizeMax();
                setUploadSizeMax(uMaxSize);
                uManager.setUploadLimit(uMaxSize.numValue);
                setUploadManagerRef({ uManager });
            }
            const shouldUseBucketList = ShouldUseBucketList();
            if (shouldUseBucketList) {
                let bucketLists = await loadBucketList();
                bucketLists = Array.isArray(bucketLists) ? bucketLists : [];
                const defaultBucket =
                    bucketLists.find((bucket) => bucket.default) ||
                    bucketLists[0];
                if (bucketLists.length === 0) {
                    setInboxFolder(InboxFolderList[DEFAULT_INBOX]);
                    setDriveBucketList(null);
                    setBucketListUsage(false);
                } else {
                    setDriveBucketList(bucketLists);
                    setInboxFolder({
                        ...defaultBucket,
                        isBucket: true,
                    });
                    setBucketListUsage(true);
                }
                setLoadingPageConfig(false);
            } else {
                setLoadingPageConfig(false);
            }

            setInitialized(true);
            const fileUploadProgress = () => {
                setUploadManagerRef({ uManager });
            };

            uManager.addEventListener(
                uManager.Events.UploadProgress,
                fileUploadProgress
            );
            return function () {
                uManager.removeEventListener(
                    uManager.Events.UploadProgress,
                    fileUploadProgress
                );
            };
        };

        init();
    }, [true]);

    return (
        <>
            {overlayStatus ? <Overlay show /> : null}
            <MainLayout
                pageTitle={languageManager.T(
                    'DRIVE_FEXPLORER_TITLE_UPLOAD_CENTER'
                )}
            >
                <Container className="upload-center-page" fluid>
                    {PermissionManager.hasVirtualAccountWithDriveUpload(
                        session,
                        profile
                    ) && (
                        <Row>
                            <Col sm={12} className="mb-2">
                                <Button
                                    varient="primary"
                                    className="btn shadow-lg mb-4"
                                    onClick={() =>
                                        navigateManager('/documents')
                                    }
                                >
                                    <Cloud {...DFLT_ICO_PROPS} />
                                    {languageManager.T(
                                        'DRIVE_FEXPLORER_TITLE_HOMEPAGE'
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col sm={6}>
                            <UploadCenterLoadingState
                                isLoading={isLoadingPageConfig}
                            >
                                {useBucketListSystem ? (
                                    <>
                                        <FileBucketSelector
                                            className="mb-4 justify-content-center"
                                            bucketList={driveBucketList}
                                            onInboxSelected={
                                                handleInboxSelected
                                            }
                                        />
                                        <FileUpload.UploadBox
                                            onAddFile={
                                                handleAddFileToUploadList
                                            }
                                            uploadMaxStr={
                                                uploadSizeMax.strValue
                                            }
                                        />
                                    </>
                                ) : (
                                    <>
                                        <h5 className="text-muted">
                                            {languageManager.T(
                                                'DRIVE_UPLOAD_SLCT_INBX'
                                            )}
                                        </h5>
                                        <FileInboxSelector
                                            className="mb-4 justify-content-center"
                                            onInboxSelected={
                                                handleInboxSelected
                                            }
                                            defaultInbox={DEFAULT_INBOX}
                                        />
                                        <FileUpload.UploadBox
                                            onAddFile={
                                                handleAddFileToUploadList
                                            }
                                            uploadMaxStr={
                                                uploadSizeMax.strValue
                                            }
                                        />
                                    </>
                                )}
                            </UploadCenterLoadingState>
                        </Col>
                        <Col sm={6}>
                            <h5 className="text-muted">
                                {languageManager.T(
                                    'SETTINGS_LABEL_UPLOAD_LIST'
                                )}
                            </h5>
                            <DSToggleButton
                                checked={isAutoUpload}
                                onChange={(value) => handleAutoUpdate(value)}
                            >
                                {languageManager.T(
                                    'SETTINGS_LABEL_AUTO_UPLOAD'
                                )}
                            </DSToggleButton>
                            <FileUpload.UploadList
                                uploadManager={uploadManagerState}
                                onRemoveFile={handleRemoveFile}
                            />
                        </Col>
                    </Row>
                </Container>
            </MainLayout>
        </>
    );
};

const mapStateToProps = (appState) => ({
    profile: appState.userProfile,
    session: appState.session,
});

export default connect(mapStateToProps)(UploadCenter);
